import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useNavigate } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import {
  reactQueryDelete,
  reactQueryPatch,
} from "@/react/helpers/backendRequestHelpers";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { UNIFIED_INBOX_MESSAGES_ROOT } from "@/react/hooks/chatsV2/messaging/useMessagingPaths";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";

import { embeddableChatApiQueryKey } from "../embeddable_chat/useEmbeddableChatsApi";
import { selectActions } from "./store";
import { useChatStore } from "./store/useChatStore";
import { useMarkChatRoomAsReadWhenVisible } from "./useMarkChatRoomAsReadWhenVisible";

const initialState = { chatRooms: [] };

const ChatRoomsContext = createContext(initialState);
ChatRoomsContext.displayName = "ChatRoomsContext";

export const ChatRoomsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const {
    fetchChatRoom,
    updateChatRoom,
    appendChatRoom,
    removeChatRoom,
    markChatRoomAsRead,
  } = useChatStore(selectActions);
  const markChatRoomAsReadWhenVisible = useMarkChatRoomAsReadWhenVisible();
  const { error } = useToast();
  const queryClient = useQueryClient();

  // Read on demand rather than subscribing: selecting the room list would
  // re-render this provider (and every context consumer) on any store write.
  const fetchChatRoomForUuid = useCallback(
    (uuid: string) => useChatStore.getState().chatRoomMap.get(uuid),
    [],
  );

  const onNewMessage = useCallback(
    (eventData: any, isOpenChatRoom: boolean) => {
      const latestChatRoomMessage: ChatRoomMessage = eventData.json_message;
      const { chat_room_uuid, parent_message_id } = latestChatRoomMessage;

      if (parent_message_id) return;
      const messageChatRoom: ChatRoom | undefined =
        fetchChatRoomForUuid(chat_room_uuid);
      if (messageChatRoom) {
        // `isOpenChatRoom` just means this room is mounted, even backgrounded;
        // the visibility check (and catch-up on return) happens inside
        // useMarkChatRoomAsReadWhenVisible.
        if (isOpenChatRoom) {
          markChatRoomAsReadWhenVisible(chat_room_uuid);
        }
      } else {
        void fetchChatRoom(chat_room_uuid);
      }
    },
    [fetchChatRoom, fetchChatRoomForUuid, markChatRoomAsReadWhenVisible],
  );

  const onChatRoomRead = useCallback(
    (eventData: any) => {
      const { chat_room_uuid: uuid } = eventData;
      markChatRoomAsRead(uuid);
    },
    [markChatRoomAsRead],
  );

  const onChatRoomCreated = useCallback(
    (eventData: any) => {
      const { chat_room: chatRoom } = eventData || {};
      appendChatRoom(chatRoom);
    },
    [appendChatRoom],
  );

  const onChatRoomUpdated = useCallback(
    (eventData: any) => {
      const { chat_room: chatRoom } = eventData || {};
      updateChatRoom(chatRoom);
    },
    [updateChatRoom],
  );

  const onChatRoomDetailsUpdated = useCallback(
    (eventData: any = {}) => {
      const { chat_room: chatRoom } = eventData;
      updateChatRoom(chatRoom);
    },
    [updateChatRoom],
  );

  const onChatRoomParticipantsUpdated = useCallback(
    (eventData: any) => {
      const updatedChatRoom =
        typeof eventData.chat_room === "object"
          ? eventData.chat_room
          : JSON.parse(eventData.chat_room);
      updateChatRoom(updatedChatRoom);

      void queryClient.invalidateQueries({
        queryKey: [embeddableChatApiQueryKey(updatedChatRoom.uuid)],
      });
    },
    [queryClient, updateChatRoom],
  );

  const onChatRoomParticipantDestroy = useCallback(
    (eventData: any) => {
      removeChatRoom(eventData.chat_room_uuid);
      const roomPath = `/messages/${eventData.chat_room_uuid}`;
      if (window.location.pathname.startsWith(roomPath)) {
        void navigate({ to: "/messages" });
      }
    },
    [navigate, removeChatRoom],
  );

  const onEventReceive = useCallback(
    (eventData: any, isOpenChatRoom: boolean) => {
      switch (eventData.event) {
        case "newMessage":
          onNewMessage(eventData, isOpenChatRoom);
          break;
        case "chatRoomRead":
          onChatRoomRead(eventData);
          break;
        case "chatRoomCreated":
          onChatRoomCreated(eventData);
          break;
        case "chatRoomUpdated":
          onChatRoomUpdated(eventData);
          break;
        case "chatRoomParticipantsUpdated":
          onChatRoomParticipantsUpdated(eventData);
          break;
        case "chatRoomParticipantDestroy":
          onChatRoomParticipantDestroy(eventData);
          break;
        case "chatRoomDeleted":
          onChatRoomParticipantDestroy(eventData);
      }
    },
    [
      onChatRoomCreated,
      onChatRoomParticipantDestroy,
      onChatRoomParticipantsUpdated,
      onChatRoomRead,
      onChatRoomUpdated,
      onNewMessage,
    ],
  );

  const onEmbedEventReceive = useCallback(
    (eventData: any) => {
      switch (eventData.event) {
        case "chatRoomRead":
          onChatRoomRead(eventData);
          break;
        case "chatRoomUpdated":
          onChatRoomDetailsUpdated(eventData);
          break;
        case "chatRoomParticipantsUpdated":
          onChatRoomParticipantsUpdated(eventData);
          break;
        case "chatRoomParticipantDestroy": {
          removeChatRoom(eventData.chat_room_uuid);
          const path = window.location.pathname;
          const dmRoomPath = `/messages/${eventData.chat_room_uuid}`;
          const inboxRoomPath = `${UNIFIED_INBOX_MESSAGES_ROOT}/${eventData.chat_room_uuid}`;
          if (path.startsWith(dmRoomPath)) {
            void navigate({ to: "/messages" });
          } else if (path.startsWith(inboxRoomPath)) {
            void navigate({ to: UNIFIED_INBOX_MESSAGES_ROOT as any });
          } else if (
            !path.startsWith("/messages") &&
            !path.startsWith(UNIFIED_INBOX_MESSAGES_ROOT)
          ) {
            window.location.reload();
          }
          break;
        }
      }
    },
    [
      navigate,
      onChatRoomDetailsUpdated,
      onChatRoomParticipantsUpdated,
      onChatRoomRead,
      removeChatRoom,
    ],
  );

  const updateRichTextMessage = useCallback(
    async ({
      chatRoomUuid,
      rich_text_body,
      messageId,
      unfurl_urls,
    }: {
      chatRoomUuid: string;
      rich_text_body: any;
      messageId: number;
      unfurl_urls: { [key: string]: boolean };
    }) => {
      const chatRoomMessageParams: any = {
        rich_text_body,
      };

      if (unfurl_urls) {
        chatRoomMessageParams["unfurl_urls"] = unfurl_urls;
      }
      try {
        await reactQueryPatch(
          internalApi.chatRoomMessages.update({
            uuid: chatRoomUuid,
            id: messageId,
          }),
          {
            chat_room_message: chatRoomMessageParams,
          },
        );
      } catch (err: any) {
        error(t("something_went_wrong"));
        notifyBugsnag(err);
      }
    },
    [error],
  );

  const deleteRichTextMessage = useCallback(
    async ({
      chatRoomUuid,
      messageId,
    }: {
      chatRoomUuid: string;
      messageId: number;
    }) => {
      try {
        await reactQueryDelete(
          internalApi.chatRoomMessages.delete({
            uuid: chatRoomUuid,
            id: messageId,
          }),
        );
      } catch (err: any) {
        error(t("something_went_wrong"));
        notifyBugsnag(err);
      }
    },
    [error],
  );

  const value: any = useMemo(
    () => ({
      fetchChatRoomForUuid,
      onEventReceive,
      onEmbedEventReceive,
      updateRichTextMessage,
      deleteRichTextMessage,
    }),
    [
      deleteRichTextMessage,
      fetchChatRoomForUuid,
      onEmbedEventReceive,
      onEventReceive,
      updateRichTextMessage,
    ],
  );

  return (
    <ChatRoomsContext.Provider value={value}>
      {children}
    </ChatRoomsContext.Provider>
  );
};

export const useChatsV2Api = () => useContext(ChatRoomsContext);
